/**
 * Semitone Piano Visualizer - Piano-roll display of 96 semitone bins
 *
 * Shows 8 octaves x 12 semitones as a piano keyboard layout with:
 * - White/black key differentiation
 * - Octave labels (A0, A1, ... A7)
 * - Optional spectrogram mode with rolling history
 * - Support for both FFT semitones and CQT
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'semitone-piano',
  displayName: 'Semitone Piano',
  contextType: '2d',
};

class SemitonePiano extends Base2DVisualizer {
  static name = 'semitone-piano';
  static displayName = 'Semitone Piano';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      {
        type: 'select',
        id: 'layout',
        label: 'Layout',
        options: [
          { value: 'horizontal', label: 'Horizontal' },
          { value: 'vertical', label: 'Vertical' },
          { value: 'spectrogram', label: 'Spectrogram' }
        ],
        default: 'horizontal'
      },
      { type: 'checkbox', id: 'useCQT', label: 'Use CQT (vs FFT)', default: false },
      { type: 'range', id: 'gain', label: 'Gain / Sensitivity', min: 50, max: 2000, default: 200, scale: 0.01 },
      { type: 'range', id: 'noiseFloor', label: 'Noise Floor', min: 0, max: 50, default: 15, scale: 0.01 },
      { type: 'range', id: 'peakSharpness', label: 'Peak Sharpness', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      { type: 'checkbox', id: 'showOctaveLabels', label: 'Show Octave Labels', default: true },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'color', id: 'whiteKeyColor', label: 'White Key Color', default: '#00ff88' },
      { type: 'color', id: 'blackKeyColor', label: 'Black Key Color', default: '#ff6600' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      layout: 'horizontal',
      useCQT: false,
      showOctaveLabels: true,
      binsPerOctave: 12,
      gain: 2.0,
      noiseFloor: 0.15,
      peakSharpness: 0.5,
    };
  }

  /**
   * Initialize state
   */
  init() {
    // Black keys in a standard octave (relative to A): A#, C#, D#, F#, G#
    // In semitone index: 1, 4, 6, 9, 11
    this.blackKeys = [1, 4, 6, 9, 11];
    this.noteNames = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'];

    // Smoothed values for all 96 semitones
    this.smoothedValues = new Float32Array(96);

    // Spectrogram history (for spectrogram mode)
    this.spectrogramHistory = [];
    this.maxHistory = 100;
  }

  /**
   * Returns the number of elements (96 semitones = 8 octaves x 12)
   */
  getElementCount() {
    return 96;
  }

  /**
   * Check if a note index (0-11) is a black key
   */
  isBlackKey(noteIndex) {
    return this.blackKeys.includes(noteIndex % 12);
  }

  /**
   * Get color for a key based on value and type
   */
  getKeyColor(value, isBlack) {
    const baseColor = isBlack ? this.config.blackKeyColor : this.config.whiteKeyColor;

    // Parse hex color
    const r = parseInt(baseColor.slice(1, 3), 16);
    const g = parseInt(baseColor.slice(3, 5), 16);
    const b = parseInt(baseColor.slice(5, 7), 16);

    // Scale brightness by value
    const brightness = 0.1 + value * 0.9;
    return `rgb(${Math.round(r * brightness)}, ${Math.round(g * brightness)}, ${Math.round(b * brightness)})`;
  }

  /**
   * Render horizontal piano layout
   */
  renderHorizontal(semitones) {
    const { ctx, canvas, config } = this;
    const padding = 40;
    const labelWidth = config.showOctaveLabels ? 35 : 0;

    const availableWidth = canvas.width - padding * 2 - labelWidth;
    const availableHeight = canvas.height - padding * 2;
    const keyWidth = availableWidth / 96;
    const keyHeight = availableHeight;

    // Draw octave labels
    if (config.showOctaveLabels) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';

      for (let oct = 0; oct < 8; oct++) {
        const x = padding + labelWidth + oct * 12 * keyWidth + 6 * keyWidth;
        ctx.fillText(`A${oct}`, x, canvas.height - 10);
      }
    }

    // Draw keys
    const startX = padding + labelWidth;
    const startY = padding;

    for (let i = 0; i < 96; i++) {
      const value = this.smoothedValues[i];
      const isBlack = this.isBlackKey(i);
      const x = startX + i * keyWidth;

      // Glow effect for active keys
      if (config.glow && value > 0.1) {
        ctx.shadowBlur = 10 + value * 15;
        ctx.shadowColor = isBlack ? config.blackKeyColor : config.whiteKeyColor;
      }

      ctx.fillStyle = this.getKeyColor(value, isBlack);
      ctx.fillRect(x, startY, keyWidth - 1, keyHeight);

      ctx.shadowBlur = 0;
    }
  }

  /**
   * Render vertical piano layout
   */
  renderVertical(semitones) {
    const { ctx, canvas, config } = this;
    const padding = 40;
    const labelHeight = config.showOctaveLabels ? 25 : 0;

    const availableWidth = canvas.width - padding * 2;
    const availableHeight = canvas.height - padding * 2 - labelHeight;
    const keyHeight = availableHeight / 96;
    const keyWidth = availableWidth;

    // Draw octave labels
    if (config.showOctaveLabels) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'left';

      for (let oct = 0; oct < 8; oct++) {
        const y = padding + (7 - oct) * 12 * keyHeight + 6 * keyHeight;
        ctx.fillText(`A${oct}`, 10, y);
      }
    }

    // Draw keys (from bottom A0 to top A7)
    const startX = padding;
    const startY = padding;

    for (let i = 0; i < 96; i++) {
      const value = this.smoothedValues[i];
      const isBlack = this.isBlackKey(i);
      // Flip Y so low frequencies are at bottom
      const y = startY + (95 - i) * keyHeight;

      if (config.glow && value > 0.1) {
        ctx.shadowBlur = 10 + value * 15;
        ctx.shadowColor = isBlack ? config.blackKeyColor : config.whiteKeyColor;
      }

      ctx.fillStyle = this.getKeyColor(value, isBlack);
      ctx.fillRect(startX, y, keyWidth, keyHeight - 1);

      ctx.shadowBlur = 0;
    }
  }

  /**
   * Render spectrogram mode with rolling history
   */
  renderSpectrogram(semitones) {
    const { ctx, canvas, config } = this;
    const padding = 40;
    const labelWidth = config.showOctaveLabels ? 35 : 0;

    // Add current frame to history
    this.spectrogramHistory.push(new Float32Array(semitones));
    if (this.spectrogramHistory.length > this.maxHistory) {
      this.spectrogramHistory.shift();
    }

    const availableWidth = canvas.width - padding * 2 - labelWidth;
    const availableHeight = canvas.height - padding * 2;
    const columnWidth = availableWidth / this.maxHistory;
    const rowHeight = availableHeight / 96;

    // Draw octave labels
    if (config.showOctaveLabels) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'right';

      for (let oct = 0; oct < 8; oct++) {
        const y = padding + (7 - oct) * 12 * rowHeight + 6 * rowHeight;
        ctx.fillText(`A${oct}`, padding + labelWidth - 5, y + 4);
      }
    }

    // Draw spectrogram
    const startX = padding + labelWidth;
    const startY = padding;

    for (let col = 0; col < this.spectrogramHistory.length; col++) {
      const frame = this.spectrogramHistory[col];
      const x = startX + col * columnWidth;

      for (let row = 0; row < 96; row++) {
        const value = frame[row];
        const isBlack = this.isBlackKey(row);
        // Flip Y so low frequencies are at bottom
        const y = startY + (95 - row) * rowHeight;

        ctx.fillStyle = this.getKeyColor(value, isBlack);
        ctx.fillRect(x, y, columnWidth, rowHeight);
      }
    }

    // Draw time indicator
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    const indicatorX = startX + (this.spectrogramHistory.length - 1) * columnWidth + columnWidth;
    ctx.beginPath();
    ctx.moveTo(indicatorX, startY);
    ctx.lineTo(indicatorX, startY + availableHeight);
    ctx.stroke();
  }

  /**
   * Apply peak sharpening - emphasize local maxima, suppress neighbors
   */
  applyPeakSharpening(values, sharpness) {
    if (sharpness <= 0) return values;

    const result = new Float32Array(values.length);

    for (let i = 0; i < values.length; i++) {
      const current = values[i];

      // Get neighbors (wrap around for edge bins)
      const prev2 = values[Math.max(0, i - 2)];
      const prev1 = values[Math.max(0, i - 1)];
      const next1 = values[Math.min(values.length - 1, i + 1)];
      const next2 = values[Math.min(values.length - 1, i + 2)];

      // Check if this is a local maximum
      const isLocalMax = current >= prev1 && current >= next1;
      const neighborAvg = (prev2 + prev1 + next1 + next2) / 4;

      if (isLocalMax && current > neighborAvg) {
        // Boost peaks
        result[i] = current;
      } else {
        // Suppress non-peaks based on sharpness
        const suppression = 1 - sharpness * 0.9; // Max 90% suppression
        result[i] = current * suppression;
      }
    }

    return result;
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    if (!features) return;

    const { config } = this;

    // Get semitone data
    let semitones = config.useCQT
      ? features.pitch.cqt(12)
      : features.pitch.semitones(12);

    // Apply gain
    const gain = config.gain || 2.0;
    const noiseFloor = config.noiseFloor || 0;
    const peakSharpness = config.peakSharpness || 0;

    // First pass: apply gain
    const processed = new Float32Array(96);
    for (let i = 0; i < 96; i++) {
      let value = semitones[i] * gain;

      // Apply noise floor (soft knee)
      if (noiseFloor > 0) {
        if (value < noiseFloor) {
          value = 0;
        } else {
          // Rescale above noise floor to use full range
          value = (value - noiseFloor) / (1 - noiseFloor);
        }
      }

      processed[i] = Math.min(1, value);
    }

    // Apply peak sharpening if enabled
    const sharpened = peakSharpness > 0
      ? this.applyPeakSharpening(processed, peakSharpness)
      : processed;

    // Apply temporal smoothing
    for (let i = 0; i < 96; i++) {
      this.smoothedValues[i] = this.smoothedValues[i] * config.smoothing +
        sharpened[i] * (1 - config.smoothing);
    }

    // Render based on layout
    switch (config.layout) {
      case 'vertical':
        this.renderVertical(this.smoothedValues);
        break;
      case 'spectrogram':
        this.renderSpectrogram(this.smoothedValues);
        break;
      case 'horizontal':
      default:
        this.renderHorizontal(this.smoothedValues);
        break;
    }

    // Draw mode indicator
    this.ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    this.ctx.font = '12px sans-serif';
    this.ctx.textAlign = 'left';
    this.ctx.fillText(
      config.useCQT ? 'CQT Mode' : 'FFT Semitones',
      10, 20
    );
  }

  /**
   * Handle configuration changes
   */
  onConfigChange(changed) {
    if (changed.layout === 'spectrogram' || changed.layout) {
      // Clear spectrogram history when switching modes
      this.spectrogramHistory = [];
    }
  }

  /**
   * Clear the canvas and reset state
   */
  clear() {
    super.clear();
    this.smoothedValues.fill(0);
    this.spectrogramHistory = [];
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(SemitonePiano);

export { SemitonePiano };
export default SemitonePiano;
